export interface RadialAction {
  id: string;
  label: string;
  icon: 'profile' | 'sit' | 'edit';
  unavailable?: string;
  run: () => void;
}

const icons = {
  profile: '<circle cx="12" cy="8" r="3"/><path d="M5 21v-3a7 7 0 0 1 14 0v3"/>',
  sit: '<path d="M6 3v11h12V9M6 9h12M6 14v7m12-7v7M4 14h16"/>',
  edit: '<path d="m4 16-1 5 5-1L21 7l-4-4L4 16Zm10-10 4 4M4 16l4 4"/>',
};

function wedge(size: number, center: number, sweep: number): string {
  const point = (radius: number, angle: number): string => `${size / 2 + Math.cos(angle) * radius} ${size / 2 + Math.sin(angle) * radius}`;
  const start = center - sweep / 2, end = center + sweep / 2, outer = size / 2 - 2, inner = size * .22, large = sweep > Math.PI ? 1 : 0;
  return `path('M ${point(outer, start)} A ${outer} ${outer} 0 ${large} 1 ${point(outer, end)} L ${point(inner, end)} A ${inner} ${inner} 0 ${large} 0 ${point(inner, start)} Z')`;
}

/** Pointer and keyboard menu. Actions are supplied by the live world controller. */
export class RadialMenu {
  private root = document.createElement('div');
  private wheel = document.createElement('div');
  private hint = document.createElement('p');
  private actions: RadialAction[] = [];
  private buttons: HTMLButtonElement[] = [];
  private events = new AbortController();
  private cancel = (): void => this.close(false);
  get isOpen(): boolean { return !this.root.hidden; }

  constructor(private stage: HTMLElement, private canvas: HTMLCanvasElement, private stopInput: () => void) {
    this.root.className = 'radial-menu'; this.root.hidden = true;
    this.wheel.className = 'radial-wheel'; this.wheel.setAttribute('role', 'menu');
    this.hint.className = 'radial-hint'; this.hint.id = 'radial-hint'; this.hint.setAttribute('role', 'status');
    this.wheel.setAttribute('aria-describedby', this.hint.id); this.root.append(this.wheel, this.hint); stage.append(this.root);
    const options = { signal: this.events.signal };
    this.root.addEventListener('contextmenu', event => event.preventDefault(), options);
    this.root.addEventListener('keydown', event => {
      if (event.key === 'Escape' || event.key === 'Tab') { event.preventDefault(); event.stopPropagation(); this.close(); return; }
      const index = this.buttons.indexOf(document.activeElement as HTMLButtonElement);
      let next: number;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.buttons.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + this.buttons.length - 1) % this.buttons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.buttons.length - 1;
      else return;
      event.preventDefault(); event.stopPropagation(); this.buttons[next]?.focus();
    }, options);
    document.addEventListener('pointerdown', event => { if (this.isOpen && !this.root.contains(event.target as Node)) this.close(false); }, { ...options, capture: true });
    window.addEventListener('blur', this.cancel, options); window.addEventListener('resize', this.cancel, options);
    document.addEventListener('visibilitychange', this.cancel, options);
  }

  open(x: number, y: number, title: string, kind: 'Avatar' | 'Object', actions: RadialAction[]): void {
    this.stopInput(); this.actions = actions; this.buttons = []; this.wheel.replaceChildren();
    const rect = this.stage.getBoundingClientRect(), size = Math.max(100, Math.min(256, rect.width - 24, rect.height - 64));
    this.root.style.setProperty('--radial-size', `${size}px`);
    this.root.style.left = `${Math.max(8, Math.min(rect.width - size - 8, x - rect.left - size / 2))}px`;
    this.root.style.top = `${Math.max(8, Math.min(rect.height - size - 50, y - rect.top - size / 2))}px`;
    this.wheel.setAttribute('aria-label', `${kind} actions`);
    actions.forEach((action, index) => {
      const angle = -Math.PI / 2 + index * Math.PI * 2 / actions.length;
      const button = document.createElement('button'); button.className = 'radial-action'; button.type = 'button'; button.dataset.action = action.id; button.setAttribute('role', 'menuitem');
      button.style.clipPath = wedge(size, angle, Math.PI * 2 / actions.length - .045);
      const label = document.createElement('span'); label.className = 'radial-action-label';
      label.style.left = `${size / 2 + Math.cos(angle) * size * .355}px`; label.style.top = `${size / 2 + Math.sin(angle) * size * .355}px`;
      label.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${icons[action.icon]}</svg>`;
      const text = document.createElement('span'); text.textContent = action.label; label.append(text); button.append(label);
      const describe = (): void => { this.hint.textContent = this.actions.find(item => item.id === action.id)?.unavailable || 'Choose an action · Esc to close'; };
      button.addEventListener('focus', describe); button.addEventListener('pointerenter', describe);
      button.addEventListener('click', () => {
        const current = this.actions.find(item => item.id === action.id);
        if (!current || current.unavailable) { describe(); return; }
        this.close(); current.run();
      });
      this.wheel.append(button); this.buttons.push(button);
    });
    const close = document.createElement('button'); close.className = 'radial-center'; close.type = 'button'; close.setAttribute('role', 'menuitem'); close.setAttribute('aria-label', 'Close menu'); close.title = title;
    const name = document.createElement('span'); name.textContent = title;
    const caption = document.createElement('small'); caption.textContent = 'Close'; close.append(name, caption);
    close.addEventListener('click', () => this.close()); this.wheel.append(close); this.buttons.push(close);
    this.root.hidden = false; this.update(actions); this.buttons[0]?.focus();
  }

  update(actions: RadialAction[]): void {
    this.actions = actions;
    for (const button of this.buttons) {
      const action = actions.find(item => item.id === button.dataset.action); if (!action) continue;
      button.setAttribute('aria-disabled', String(!!action.unavailable)); button.title = action.unavailable || action.label;
      if (button === document.activeElement) this.hint.textContent = action.unavailable || 'Choose an action · Esc to close';
    }
  }

  close(restoreFocus = true): void {
    if (!this.isOpen) return;
    this.root.hidden = true; this.actions = [];
    if (restoreFocus) this.canvas.focus();
  }

  dispose(): void { this.events.abort(); this.root.remove(); }
}
